/** ASIS BLE v1: the radio transports only a server-encrypted envelope.
 * Advertising names/INFO are discovery hints, never proof of ownership. */
import { validDeviceId } from '../../protocol/device-id.js';
export const ASIS_BLE = Object.freeze({
  service: 'c8a10001-8c5e-4b7c-9f6d-6b5e3b83d001',
  info: 'c8a10002-8c5e-4b7c-9f6d-6b5e3b83d001',
  rx: 'c8a10003-8c5e-4b7c-9f6d-6b5e3b83d001',
  tx: 'c8a10004-8c5e-4b7c-9f6d-6b5e3b83d001',
});
/** `maxNets` is how many Wi-Fi networks the unit can keep (protocol v2); absent means one.
 * `codeCheck` (ASIS One, unowned only): the unit shows a code on its screen; the app proves it to the server. */
export interface ProvisioningInfo { v: 1; id: string; nonce: string; expiresIn: number; owned: boolean; hasWifi: boolean; maxNets?: number; codeCheck?: string }
export interface ProvisioningEnvelope { v: 1; id: string; nonce: string; iv: string; ciphertext: string; tag: string }
export type ReceiptStatus = 'saved' | 'wifi_failed' | 'storage_failed';
export interface ProvisioningReceipt { v: 1; id: string; nonce: string; status: ReceiptStatus; mac: string }
export interface BleCharacteristic extends EventTarget {
  value?: DataView;
  readValue(): Promise<DataView>;
  writeValueWithResponse(value: Uint8Array): Promise<void>;
  startNotifications(): Promise<unknown>;
  stopNotifications(): Promise<unknown>;
}
export interface BleDevice extends EventTarget {
  gatt?: {
    connected: boolean;
    connect(): Promise<{ getPrimaryService(id: string): Promise<{ getCharacteristic(id: string): Promise<BleCharacteristic> }> }>;
    disconnect(): void;
  };
}
export interface BluetoothAdapter { requestDevice(options: { filters: Array<{ services: string[] }> }): Promise<BleDevice> }
export interface BluetoothChoice { deviceId: string; deviceName: string }
export interface BluetoothChooserBridge {
  prepare(service: string): Promise<{ requestId: string }>;
  select(requestId: string, deviceId: string): Promise<void>;
  cancel(requestId: string): Promise<void>;
  onDevices(listener: (event: { requestId: string; devices: BluetoothChoice[] }) => void): () => void;
}
export class BleSetupError extends Error {
  constructor(public readonly code: 'cancelled' | 'timeout' | 'invalid_info' | 'unsupported' | 'disconnected' | 'invalid_receipt' | 'expired' | 'invalid_envelope' | 'account_changed' | 'wrong_code') { super(code); }
}
const encoder = new TextEncoder();
const decoder = new TextDecoder('utf-8', { fatal: true });
const isRecord = (value: unknown): value is Record<string, unknown> => Boolean(value && typeof value === 'object' && !Array.isArray(value));
export function isBase64url(value: unknown, bytes?: number): value is string {
  if (typeof value !== 'string' || !/^[A-Za-z0-9_-]+$/u.test(value)) return false;
  try { const raw = atob(value.replace(/-/gu, '+').replace(/_/gu, '/')); return (bytes === undefined || raw.length === bytes) && btoa(raw).replace(/\+/gu, '-').replace(/\//gu, '_').replace(/=+$/u, '') === value; }
  catch { return false; }
}
export function parseProvisioningInfo(value: DataView): ProvisioningInfo {
  try {
    if (value.byteLength > 512) throw new Error();
    const info: unknown = JSON.parse(decoder.decode(value));
    if (!isRecord(info) || info.v !== 1 || !validDeviceId(info.id) || !isBase64url(info.nonce, 32) || typeof info.owned !== 'boolean' ||
      typeof info.hasWifi !== 'boolean' || typeof info.expiresIn !== 'number' || !Number.isFinite(info.expiresIn) || info.expiresIn <= 0 || info.expiresIn > 300) throw new Error();
    const parsed: ProvisioningInfo = { v: 1, id: info.id, nonce: info.nonce, expiresIn: info.expiresIn, owned: info.owned, hasWifi: info.hasWifi };
    if (info.maxNets !== undefined) {
      if (typeof info.maxNets !== 'number' || !Number.isInteger(info.maxNets) || info.maxNets < 1) throw new Error();
      parsed.maxNets = Math.min(info.maxNets, 5);
    }
    if (info.screenCode !== undefined) {
      if (info.screenCode !== true || info.owned || !isBase64url(info.codeCheck, 32)) throw new Error();
      parsed.codeCheck = info.codeCheck;
    }
    return parsed;
  } catch { throw new BleSetupError('invalid_info'); }
}
export function parseReceipt(text: string, info: ProvisioningInfo): ProvisioningReceipt {
  try {
    const receipt: unknown = JSON.parse(text);
    if (!isRecord(receipt) || receipt.v !== 1 || receipt.id !== info.id || receipt.nonce !== info.nonce ||
      !['saved', 'wifi_failed', 'storage_failed'].includes(String(receipt.status)) || !isBase64url(receipt.mac, 32)) throw new Error();
    return { v: 1, id: info.id, nonce: info.nonce, status: receipt.status as ReceiptStatus, mac: receipt.mac };
  } catch { throw new BleSetupError('invalid_receipt'); }
}
export function encodeEnvelope(envelope: ProvisioningEnvelope, info: ProvisioningInfo): Uint8Array {
  if (envelope.v !== 1 || envelope.id !== info.id || envelope.nonce !== info.nonce || !isBase64url(envelope.iv, 12) ||
    !isBase64url(envelope.tag, 16) || !isBase64url(envelope.ciphertext)) throw new BleSetupError('invalid_envelope');
  // Whitelist fields so an unexpected server response cannot add commands/secrets.
  const bytes = encoder.encode(JSON.stringify({ v: 1, id: envelope.id, nonce: envelope.nonce, iv: envelope.iv, ciphertext: envelope.ciphertext, tag: envelope.tag }) + '\n');
  if (bytes.length > 2048) throw new BleSetupError('invalid_envelope');
  return bytes;
}
export function validateWifi(ssid: string, psk: string): boolean {
  const nameLength = encoder.encode(ssid).length;
  const keyLength = encoder.encode(psk).length;
  const validText = (value: string) => !/[\0\r\n]/u.test(value) && decoder.decode(encoder.encode(value)) === value;
  return nameLength >= 1 && nameLength <= 32 && validText(ssid) && validText(psk) &&
    (keyLength === 0 || (keyLength >= 8 && keyLength <= 63) || /^[a-fA-F0-9]{64}$/u.test(psk));
}
// Chromium can return the same BluetoothDevice/GATT object to a later attempt.
// A cancelled attempt must only clean up connections it still owns.
const gattOwners = new WeakMap<BleDevice, object>();
function cancelled(): BleSetupError { return new BleSetupError('cancelled'); }
export function abortable<T>(work: Promise<T>, signal: AbortSignal): Promise<T> {
  if (signal.aborted) { void work.catch(() => undefined); return Promise.reject(cancelled()); }
  return new Promise((resolve, reject) => {
    const stop = () => reject(cancelled());
    signal.addEventListener('abort', stop, { once: true });
    work.then(value => { if (!signal.aborted) resolve(value); }, reject).finally(() => signal.removeEventListener('abort', stop));
  });
}
export function delay(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) { reject(cancelled()); return; }
    const stop = () => { clearTimeout(timer); reject(cancelled()); };
    const timer = setTimeout(() => { signal.removeEventListener('abort', stop); resolve(); }, ms);
    signal.addEventListener('abort', stop, { once: true });
  });
}
export class DeviceBleTransport {
  private readonly lifetime = new AbortController();
  private readonly gattOwner = {};
  private device: BleDevice | null = null;
  private chooserRequestId: string | null = null;
  private rx: BleCharacteristic | null = null;
  private tx: BleCharacteristic | null = null;
  private info: ProvisioningInfo | null = null;
  private deadline = 0;
  private sending = false;
  private ownsDevice(device = this.device): boolean { return Boolean(device && gattOwners.get(device) === this.gattOwner); }
  private disconnectDevice(device = this.device): void { if (this.ownsDevice(device)) device?.gatt?.disconnect(); }
  private disconnectListener = () => { if (this.ownsDevice()) { this.lifetime.abort(); this.onDisconnected?.(); } };
  constructor(private readonly bluetooth: BluetoothAdapter, private readonly chooser?: BluetoothChooserBridge, private readonly onDisconnected?: () => void) {}

  async connect(signal: AbortSignal): Promise<ProvisioningInfo> {
    const operation = AbortSignal.any([signal, this.lifetime.signal, AbortSignal.timeout(90_000)]);
    try {
      if (this.chooser) {
        const preparing = this.chooser.prepare(ASIS_BLE.service);
        void preparing.then(result => {
          this.chooserRequestId = result.requestId;
          if (operation.aborted) void this.chooser?.cancel(result.requestId).catch(() => undefined);
        }, () => undefined);
        await abortable(preparing, operation);
      }
      const selecting = this.bluetooth.requestDevice({ filters: [{ services: [ASIS_BLE.service] }] });
      // requestDevice does not open GATT. Ignore a late selection rather than
      // disconnecting a shared device that may belong to a newer attempt.
      const device = await abortable(selecting, operation);
      if (operation.aborted) throw cancelled();
      this.device = device;
      if (!device.gatt) throw new BleSetupError('unsupported');
      gattOwners.set(device, this.gattOwner);
      device.addEventListener('gattserverdisconnected', this.disconnectListener);
      const connecting = device.gatt.connect();
      void connecting.then(() => { if (operation.aborted) this.disconnectDevice(device); }, () => undefined);
      const server = await abortable(connecting, operation);
      const service = await abortable(server.getPrimaryService(ASIS_BLE.service), operation);
      const info = await abortable(service.getCharacteristic(ASIS_BLE.info), operation);
      this.info = parseProvisioningInfo(await abortable(info.readValue(), operation));
      this.deadline = performance.now() + this.info.expiresIn * 1000;
      this.rx = await abortable(service.getCharacteristic(ASIS_BLE.rx), operation);
      this.tx = await abortable(service.getCharacteristic(ASIS_BLE.tx), operation);
      return this.info;
    } catch (error) { this.disconnect(); throw error; }
  }

  async send(envelope: ProvisioningEnvelope, signal: AbortSignal): Promise<ProvisioningReceipt> {
    const { info, rx, tx } = this;
    if (!info || !rx || !tx || !this.ownsDevice() || !this.device?.gatt?.connected || this.sending) throw new BleSetupError('disconnected');
    if (performance.now() >= this.deadline) throw new BleSetupError('expired');
    const bytes = encodeEnvelope(envelope, info);
    const operation = AbortSignal.any([signal, this.lifetime.signal, AbortSignal.timeout(75_000)]);
    this.sending = true;
    let pending = '';
    let received: ProvisioningReceipt | null = null;
    let invalid: BleSetupError | null = null;
    const notify: EventListener = event => {
      const value = (event.target as BleCharacteristic).value;
      if (!value || received || invalid) return;
      try {
        const chunk = decoder.decode(value);
        // readValue also emits characteristicvaluechanged; a complete read
        // replaces any partial notification instead of concatenating both.
        if (chunk.startsWith('{') && chunk.endsWith('\n')) { received = parseReceipt(chunk.trim(), info); return; }
        pending += chunk;
        if (pending.length > 2048) { invalid = new BleSetupError('invalid_receipt'); return; }
        const end = pending.indexOf('\n');
        if (end >= 0) received = parseReceipt(pending.slice(0, end), info);
      } catch { pending = ''; /* A dropped notification can corrupt a frame; the complete read below is authoritative. */ }
    };
    tx.addEventListener('characteristicvaluechanged', notify);
    try {
      await abortable(tx.startNotifications(), operation);
      for (let offset = 0; offset < bytes.length; offset += 20) {
        if (performance.now() >= this.deadline) throw new BleSetupError('expired');
        await abortable(rx.writeValueWithResponse(bytes.slice(offset, offset + 20)), operation);
      }
      while (true) {
        if (invalid) throw invalid;
        if (received) return received;
        // Read is the documented recovery path if a notification was lost.
        const current = await abortable(tx.readValue(), operation);
        if (current.byteLength) {
          if (current.byteLength > 2048) throw new BleSetupError('invalid_receipt');
          const text = decoder.decode(current);
          if (text.includes('\n')) return parseReceipt(text.slice(0, text.indexOf('\n')), info);
        }
        await delay(1000, operation);
      }
    } catch (error) {
      if (signal.aborted) throw cancelled();
      if (this.lifetime.signal.aborted) throw new BleSetupError('disconnected');
      if (operation.aborted) throw new BleSetupError('timeout');
      throw error;
    } finally {
      bytes.fill(0); pending = ''; this.sending = false;
      tx.removeEventListener('characteristicvaluechanged', notify);
      if (this.ownsDevice() && this.device?.gatt?.connected) void tx.stopNotifications().catch(() => undefined);
    }
  }

  disconnect(): void {
    this.lifetime.abort();
    this.device?.removeEventListener('gattserverdisconnected', this.disconnectListener);
    this.disconnectDevice();
    this.device = null; this.rx = null; this.tx = null; this.info = null;
    if (this.chooserRequestId) void this.chooser?.cancel(this.chooserRequestId).catch(() => undefined);
    this.chooserRequestId = null;
  }
}
